/**
 * Clickwrap consent gate.
 *
 * Rendered over the authenticated app when GET /api/me reports
 * `user.needsTermsAcceptance` (no acceptance, or an older Terms version). It is
 * intentionally NOT dismissible — the user must affirmatively agree before using
 * the app. Acceptance is recorded server-side (version + timestamp + IP) via
 * POST /api/me/accept-terms.
 */
import { useState } from 'react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Loader2, ArrowRight } from 'lucide-react';
import { meAPI } from '../../services/api';

interface TermsGateProps {
  open: boolean;
  onAccepted: () => void;
}

export function TermsGate({ open, onAccepted }: TermsGateProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const accept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await meAPI.acceptTerms();
      onAccepted();
    } catch {
      setError('Could not record your acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border bg-card text-card-foreground shadow-xl p-6 space-y-4">
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold">Before you continue</h2>
          <p className="text-sm text-muted-foreground">
            To use 4K Labs, please review and agree to our terms.
          </p>
        </div>

        <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
          <Checkbox
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
            disabled={submitting}
            className="mt-0.5"
          />
          <span className="text-sm leading-relaxed">
            I have read and agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">Terms of Service</a>,{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">Privacy Policy</a>, and{' '}
            <a href="/refund" target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">Refund Policy</a>.
          </span>
        </label>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button onClick={accept} disabled={!agreed || submitting} className="w-full">
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
          ) : (
            <>Agree and continue <ArrowRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
